import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import Layout from '../components/Layout';
import 'leaflet/dist/leaflet.css';
import SearchSelect from './SearchSelect';
import { DatePicker } from '../components/Planning/components/DatePicker';
import { useVanoCodes } from '../context/VanoCodesContext';
import { activityLabel, ACTIVITY_CATEGORIES } from '../utils/vanoCodes';
import { parseGps } from '../utils/gps';
import { TRIAL_MAX_LENGTH, NON_NEGATIVE_NUMBER, YEAR_NUMBER, FIELD_PHASE_START_MONTH, FIELD_PHASE_END_MONTH } from '../utils/trialFieldLimits';
import { yearWarning } from '../utils/yearWarning';
import { parseYMDToLocalDate, formatDateToYMD, formatDate, TIMING_YEAR_START, TIMING_YEAR_END } from '../utils/dateFormat';
import { wizardTaskGroups, diaryDatesFor, copiedIntervals } from '../utils/wizardTasks';
import { CO_LABEL, coLabel, sortContracts } from '../utils/contractLabel';
import ContractModal from '../components/ContractModal';
import ProjectModal from '../components/ProjectModal';
import HostModal from '../components/HostModal';
import TriStateSelect from '../components/TriStateSelect';
import PlannedInput from '../components/PlannedInput';
import { expectedText } from '../utils/planning';
import { TRI_STATE_FIELDS } from '../utils/triState';
import { SAMPLE_FIELDS, shownSamples, takesSamples } from '../utils/samples';

const getId = (o) => o?.id;

// The customer's expected dates of the tasks (step 5): as the date in Add Diary Entry
const EXPECTED_START_MONTH = new Date(TIMING_YEAR_START, 0, 1);
const EXPECTED_END_MONTH = new Date(TIMING_YEAR_END, 11, 31);

// Step 6: the name of a chosen item (crop, host, user ...) from its list, '' when none is chosen
const reviewName = (list, id, label = (item) => item.name) => {
  const item = id ? list.find((x) => String(x.id) === String(id)) : null;
  return item ? label(item) : '';
};
const reviewPerson = (user) => (user.initials ? `${user.name} (${user.initials})` : (user.name || user.email || ''));

export default function TrialNew() {
  const vanoCodes = useVanoCodes();
  /* Custom scrollbar for customer list */
  const customScrollbarStyle = `
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #34d399;
  border-radius: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #34d399 #f3f4f6;
}
`;
  const navigate = useNavigate();
  // Inject custom scrollbar style once
  useEffect(() => {
    if (!document.getElementById('custom-scrollbar-style')) {
      const style = document.createElement('style');
      style.id = 'custom-scrollbar-style';
      style.innerHTML = customScrollbarStyle;
      document.head.appendChild(style);
    }
  }, []);
  const location = useLocation();
  const trialId = location.state?.trialId;
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [contracts, setContracts] = useState([]);
  // Step 2's pick list: newest year first (shown as "PO {co} – {description} ({year})")
  const sortedContracts = useMemo(() => sortContracts(contracts), [contracts]);
  // "New PO / project / host" dialogs, and why creating the trial failed (shown above the button)
  const [newDialog, setNewDialog] = useState(null); // 'po' | 'project' | 'host' | null
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debounceTimeout = useRef();
  const [projects, setProjects] = useState([]);
  const [trialForm, setTrialForm] = useState({});
  // Year follows the chosen PO's year until the user changes it; only then is a different PO year a warning
  const [yearTouched, setYearTouched] = useState(false);
  const [trialFetchError, setTrialFetchError] = useState(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // New state for reference data
  const [countries, setCountries] = useState([]);
  const [crops, setcrops] = useState([]);
  const [studyTypes, setStudyTypes] = useState([]);
  const [trialTypes, setTrialTypes] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [studyDirectors, setStudyDirectors] = useState([]);

  const steps = [
    { id: 1, title: 'Step 1', description: 'Select Customer' },
    { id: 2, title: 'Step 2', description: `Select ${CO_LABEL}` },
    { id: 3, title: 'Step 3', description: 'Select Project' },
    { id: 4, title: 'Step 4', description: 'Trial setup' },
    { id: 5, title: 'Step 5', description: 'Tasks & time intervals' },
    { id: 6, title: 'Step 6', description: 'Review & create' },
  ];

  // Step 5: the tasks the trial will get (they follow step 4) and the Time interval of each, by VA No: its date
  // and/or its text
  const [diaryDates, setDiaryDates] = useState({});
  const [diaryTexts, setDiaryTexts] = useState({});
  const taskGroups = useMemo(() => wizardTaskGroups(trialForm, vanoCodes, ACTIVITY_CATEGORIES), [trialForm, vanoCodes]);
  const taskCount = taskGroups.reduce((n, group) => n + group.tasks.length, 0);
  const datedCount = new Set([...Object.keys(diaryDatesFor(taskGroups, diaryDates)), ...Object.keys(diaryDatesFor(taskGroups, diaryTexts))]).size;
  const taskInterval = (vano) => ({ timeinterval: diaryDates[vano] || '', timeinterval_text: diaryTexts[vano] || '' });
  // Step 4 sets the Study Director to the signed-in user on the first arrival only: coming back from step 5
  // keeps the one chosen
  const leftStep4 = useRef(false);

  const token = localStorage.getItem('token');

  // Set study director when component mounts
  useEffect(() => {
    try {
      const userInfoStr = sessionStorage.getItem('userInfo');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        const currentUserId = userInfo?.data?.id;

        if (currentUserId) {
          setTrialForm(f => ({ ...f, studydirector: currentUserId }));
          console.log('Initially setting study director to current user:', currentUserId);
        }
      }
    } catch (error) {
      console.error('Error parsing userInfo during initial setup:', error);
    }
  }, []);

  // Fetch trial data if trialId is present (copy mode)
  useEffect(() => {
    if (trialId) {
      setTrialFetchError(null);
      axios.post(
        `${config.apiBaseUrl}/trials/${trialId}`,
        {
          // (the API sends the diaries anyway; their Time intervals go into step 5)
          exclude_relations: ['attachments']
        },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      )
        .then(async res => {
          const trial = res.data;
          console.log('API trial copy response:', trial);

          // Extract customer info from the trial data
          let customerObj = null;
          if (trial.customer) {
            customerObj = {
              id: trial.customer.id,
              label: trial.customer.name,
              value: trial.customer.id,
              ...trial.customer
            };
          }

          // Extract contract info from the project data
          let contractObj = null;
          if (trial.project && trial.project.contract) {
            contractObj = {
              id: trial.project.contract.id,
              co: trial.project.contract.co,
              customer_id: trial.project.contract.customer_id,
              description: trial.project.contract.description,
              year: trial.project.contract.year
            };
          }

          // Extract project info
          let projectObj = null;
          if (trial.project) {
            projectObj = {
              id: trial.project.id,
              protocolno: trial.project.protocolno,
              contract_id: trial.project.contract_id
            };
          }

          // Set up formData for steps 1-3
          setFormData({
            customer: customerObj,
            contract: contractObj,
            project: projectObj
          });

          // Remove trialno (do not prefill it), id, and any diaries or attachments
          const { trialno, id, customer, project, diaries, attachments, diary, attachment, ...rest } = trial;

          // Step 5 starts with each task's Time interval from the trial copied - never its Planned or Done data
          const { dates, texts } = copiedIntervals(diaries);
          setDiaryDates(dates);
          setDiaryTexts(texts);

          // Set up trialForm for step 4, excluding trialno and attachments/diaries
          setTrialForm({
            ...rest,
            customer_id: customerObj?.id || trial.customer_id,
            project_id: projectObj?.id || trial.project_id,
            projectno: projectObj?.protocolno || '',
            studydirector: trial.studydirector?.id || trial.studydirector || localStorage.getItem('userId') || '',
            trialno: '', // always blank for new trial
            // Add any nested objects that need to be flattened
            country_id: trial.country?.id || trial.country_id,
            crop_id: trial.crop?.id || trial.crop_id,
            study_type_id: trial.study_type?.id || trial.study_type_id,
            trial_type_id: trial.trial_type?.id || trial.trial_type_id,
            host_id: trial.host?.id || trial.host_id,
          });

          // Ensure step 3 is marked as completed if project exists
          if (projectObj && projectObj.id) {
            setStep(4);
          } else if (contractObj && contractObj.id) {
            setStep(3);
          } else if (customerObj && customerObj.id) {
            setStep(2);
          } else {
            setStep(1);
          }
        })
        .catch(err => {
          setTrialFetchError('Failed to fetch trial for copy. Please try again or contact support.');
          setStep(1);
          setFormData({});
          setTrialForm({});
          console.error('Failed to fetch trial for copy:', err);
        });
    }
  }, [trialId]);

  // Year is the chosen PO's year until the user changes it
  useEffect(() => {
    if (yearTouched || !formData.contract) return;
    setTrialForm(f => ({ ...f, Year: formData.contract.year ?? null }));
  }, [formData.contract, yearTouched]);

  useEffect(() => {
    if (step === 1) {
      axios.post(`${config.apiBaseUrl}/masterdata/customers`, { active_only: true }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
        .then(response => {
          const customerData = response.data.map(customer => ({
            id: customer.id,
            label: customer.name,
            value: customer.id,
          }));
          // Sort by name ASC
          customerData.sort((a, b) => a.label.localeCompare(b.label));
          setCustomers(customerData);
          setFilteredCustomers(customerData);
        })
        .catch(error => console.error('Error fetching customers:', error));
    }
  }, [step]);

  // Fetch contracts when moving to step 2 and a customer is selected
  useEffect(() => {
    if (step === 2 && formData.customer && formData.customer.id) {
      axios.post(`${config.apiBaseUrl}/masterdata/contracts`, { customer_id: formData.customer.id, active_only: true }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
        .then(response => setContracts(response.data))
        .catch(error => setContracts([]));
    }
  }, [step, formData.customer, token]);

  // Fetch projects when moving to step 3 and a contract is selected
  useEffect(() => {
    if (step === 3 && formData.contract && formData.contract.id) {
      axios.post(`${config.apiBaseUrl}/masterdata/contracts/${formData.contract.id}/projects`, { active_only: true }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
        .then(response => setProjects(response.data))
        .catch(error => setProjects([]));
    }
  }, [step, formData.contract, token]);

  // Fetch reference data when reaching step 4
  useEffect(() => {
    if (step === 4) {
      // Ensure default status flags are set when reaching final step
      // All boolean fields must have defaults to avoid sending null to backend
      // Booleans are converted to 0 (false) or 1 (true) for backend
      setTrialForm(f => ({
        ...f, // preserve all existing fields first
        // Boolean fields with logical defaults based on trial workflow
        // These are checked by default (will be 1):
        Destruction: f.Destruction ?? true,           // 1 - default checked
        StOut: f.StOut ?? true,                       // 1 - default checked
        PegsUp: f.PegsUp ?? true,                     // 1 - default checked
        TrialDone: f.TrialDone ?? true,               // 1 - default checked
        DraftReport: f.DraftReport ?? true,           // 1 - default checked
        Borders_Sprayed: f.Borders_Sprayed ?? true,   // 1 - default checked
        ARMReady: f.ARMReady ?? true,                 // 1 - default checked
        FinalReport: f.FinalReport ?? true,           // 1 - default checked
        ReportProofRead: f.ReportProofRead ?? true,   // 1 - default checked
        // These are unchecked by default (will be 0):
        RecordCleansing: f.RecordCleansing ?? false,  // 0 - default unchecked
        TrialSetupARM: f.TrialSetupARM ?? false,      // 0 - default unchecked
        ResAr: f.ResAr ?? false,                      // 0 - default unchecked
        PlotSize: f.PlotSize ?? false,                // 0 - default unchecked
        WOA: f.WOA ?? false,                          // 0 - default unchecked
        GerminationTest: f.GerminationTest ?? 2,      // 2 - To be decided, as Harvest
        NIT: f.NIT ?? 2,                              // 2 - To be decided, as Harvest
        ReadyForBilling: f.ReadyForBilling ?? false,  // 0 - default unchecked
        AdditionalWork: f.AdditionalWork ?? false,    // 0 - default unchecked
        TGW: f.TGW ?? 2,                              // 2 - To be decided, as Harvest
        HLLWonly: f.HLLWonly ?? false,                // 0 - default unchecked
        EndFieldPhase: f.EndFieldPhase ?? false,      // 0 - default unchecked
        // Special case: Harvest is integer (0/1/2), default to 2 (To be decided)
        Harvest: f.Harvest ?? 2,
      }));
      const token = localStorage.getItem('token');

      // Fetch countries
      axios.post(`${config.apiBaseUrl}/masterdata/countries`, { active_only: true }, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      })
        .then(response => setCountries(response.data))
        .catch(error => console.error('Error fetching countries:', error));

      // Fetch crops
      axios.post(`${config.apiBaseUrl}/masterdata/crops`, { active_only: true }, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      })
        .then(response => setcrops(response.data))
        .catch(error => console.error('Error fetching crops:', error));

      // Fetch study types
      axios.post(`${config.apiBaseUrl}/masterdata/studytypes`, { active_only: true }, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      })
        .then(response => setStudyTypes(response.data))
        .catch(error => console.error('Error fetching study types:', error));

      // Fetch trial types - Using the correct endpoint /masterdata/trialtype
      axios.post(`${config.apiBaseUrl}/masterdata/trialtype`, { active_only: true }, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      })
        .then(response => {
          console.log('Trial types response:', response.data);
          setTrialTypes(response.data);
        })
        .catch(error => {
          console.error('Error fetching trial types:', error);
          // Try alternative endpoints if the first one fails
          axios.post(`${config.apiBaseUrl}/masterdata/trialtypes`, {}, {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          })
            .then(response => {
              console.log('Alternative trial types response:', response.data);
              setTrialTypes(response.data);
            })
            .catch(altError => {
              console.error('Error fetching trial types with first alternative endpoint:', altError);
              axios.post(`${config.apiBaseUrl}/masterdata/trial_types`, {}, {
                headers: { Authorization: token ? `Bearer ${token}` : undefined },
              })
                .then(response => {
                  console.log('Second alternative trial types response:', response.data);
                  setTrialTypes(response.data);
                })
                .catch(altError2 => console.error('Error fetching trial types with second alternative endpoint:', altError2));
            });
        });

      // Fetch hosts
      axios.post(`${config.apiBaseUrl}/masterdata/hosts`, { active_only: true }, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      })
        .then(response => setHosts(response.data))
        .catch(error => console.error('Error fetching hosts:', error));

      // Fetch study directors from users endpoint
      axios.post(`${config.apiBaseUrl}/masterdata/users`, { active_only: true }, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      })
        .then(response => {
          console.log('Study directors response:', response.data);
          setStudyDirectors(response.data);

          // Auto-select current user as study director if not already set
          const currentUserId = localStorage.getItem('userId');
          if (currentUserId && (!trialForm.studydirector || trialForm.studydirector === '')) {
            setTrialForm(f => ({ ...f, studydirector: currentUserId }));
          }
        })
        .catch(error => {
          console.error('Error fetching study directors from users:', error);
          // Try the alternative endpoint as fallback
          axios.post(`${config.apiBaseUrl}/masterdata/persons`, { role: 'director' }, {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          })
            .then(response => {
              console.log('Alternative study directors response:', response.data);
              setStudyDirectors(response.data);
            })
            .catch(altError => console.error('Error fetching study directors with alternative endpoint:', altError));
        });
    }
  }, [step]);

  const handleNext = () => {
    if (step === 4) leftStep4.current = true;
    if (step < steps.length) setStep(step + 1);
  };

  const handlePrevious = () => {
    if (step > 1) setStep(step - 1);
  };

  // Customer → PO → project: choosing another customer clears the PO and project, another PO clears
  // the project (they must belong together - the API checks it too)
  const handleFormChange = (key, value) => {
    const changed = String(formData[key]?.id ?? '') !== String(value?.id ?? '');
    const clearsProject = changed && (key === 'customer' || key === 'contract');

    setFormData(prev => {
      const next = { ...prev, [key]: value };
      if (changed && key === 'customer') next.contract = null;
      if (clearsProject) next.project = null;
      return next;
    });

    setTrialForm(f => {
      const next = { ...f };
      if (key === 'customer') next.customer_id = value?.id ?? null;
      if (key === 'project') {
        next.project_id = value?.id ?? null;
        next.projectno = value?.protocolno || '';
      }
      // A new project gets a new suggested trial number (see the next-trial-number effect)
      if (clearsProject || (key === 'project' && changed)) next.trialno = '';
      if (clearsProject) { next.project_id = null; next.projectno = ''; }
      return next;
    });
  };


  const handleCustomerSelect = (customer) => {
    handleFormChange('customer', customer);
    setTrialForm(f => ({ ...f, customer_id: customer.id }));
    setSearchTerm(customer.label || "");
    setCommandPaletteOpen(false);
  };

  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  // Debounced filtering of customers
  useEffect(() => {
    if (formData.customer) return; // Don't filter if a customer is selected
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      const lowerCaseTerm = searchTerm.toLowerCase();
      setFilteredCustomers(
        customers.filter((customer) =>
          customer.label.toLowerCase().includes(lowerCaseTerm)
        )
      );
    }, 500);
    return () => clearTimeout(debounceTimeout.current);
  }, [searchTerm, customers, formData.customer]);


  // List of all fillable fields for trial creation (from backend)
  const fillableFields = [
    'customer_id', 'project_id', 'projectno', 'trialno', 'studydirector', 'pitxt', 'subcontractor', 'testitem', 'country_id', 'crop_id', 'locationtarget', 'study_type_id', 'trial_type_id', 'host_id', 'TrialAddress', 'LocalReference', 'EarthSample', 'TreatmentCount', 'ReplicationCount', 'ApplicationCount', 'VisitCount', 'Harvest', 'Destruction', 'FieldFaseStart', 'FieldFaseEnd', 'TrialSetupARM', 'CustomerContact', 'PlotSizeL', 'PlotSizeB', 'Year', 'subscribed_GEP_GL', 'unsubscribed_GEP_GL', 'Borders_Sprayed', 'Weighed_out_Applications', 'TS', 'SubcEPA', 'StOut', 'ResAr', 'FinalReport', 'PegsUp', 'PlotSize', 'WOA', 'PO', 'Noter1', 'Noter2', 'Noter3', 'PosGPSLatitude', 'PosGPSLongitude', 'DraftReport', 'GerminationTest', 'NIT', 'ReadyForBilling', 'AdditionalWork', 'TGW', 'RecordCleansing', 'HLLWonly', 'EndFieldPhase', 'ARMReady', 'ReportProofRead', 'TrialDone', 'NIT_Samples', 'GT_Samples', 'TGW_Samples', 'Contract_Amount', 'Hours', 'FT', 'PI'
  ];

  // Create trial handler: post all fields, filled or not
  const handleCreateTrial = async () => {
    const payload = {};

    // Map form fields to payload, defaulting to null if not present
    fillableFields.forEach(field => {
      // Map from formData/trialForm as appropriate
      if (field === 'customer_id') {
        payload[field] = formData.customer?.id || trialForm.customer_id || null;
      } else if (field === 'project_id') {
        payload[field] = formData.project?.id || trialForm.project_id || null;
      } else if (field === 'trialno') {
        // Ensure trialno is included
        payload[field] = trialForm[field] || '';
      } else if (field === 'studydirector') {
        // Ensure studydirector is included
        payload[field] = trialForm[field] || localStorage.getItem('userId') || null;
      } else {
        // For boolean fields, convert to 0 or 1 (backend expects integer)
        if (typeof trialForm[field] === 'boolean') {
          payload[field] = trialForm[field] ? 1 : 0;
        } else {
          payload[field] = trialForm[field] !== undefined ? trialForm[field] : null;
        }
      }
    });

    // Samples only for an activity that is Yes or To be decided (a number typed before it was set to No is dropped)
    SAMPLE_FIELDS.forEach(({ field, flag }) => {
      if (!takesSamples(trialForm, flag) || payload[field] === '') payload[field] = null;
    });

    // The chosen PO, so the API can check that customer, PO and project belong together (it sets the
    // trial's PO itself, from the project's contract)
    payload.contract_id = formData.contract?.id ?? null;
    delete payload.PO;

    // The customer's expected dates of the tasks the trial gets (step 5), set in the same transaction
    payload.diary_dates = diaryDatesFor(taskGroups, diaryDates);
    payload.diary_texts = diaryDatesFor(taskGroups, diaryTexts);

    // Explicitly ensure diaries and attachments are not included
    delete payload.diaries;
    delete payload.attachments;
    delete payload.diary;
    delete payload.attachment;

    console.log('Submitting trial data:', payload);

    setCreateError('');
    setCreating(true);
    try {
      // Get fresh token to ensure it's current
      const freshToken = localStorage.getItem('token');

      // If trialno is not set, let the backend handle it
      // The backend will auto-generate based on the project number
      if (!payload.trialno && payload.project_id) {
        console.log('No trial number provided. Backend will auto-generate one.');
      }

      // Add exclude_relations to payload to tell backend not to include diaries and attachments
      payload.exclude_relations = ['diaries', 'attachments', 'diary', 'attachment'];

      const res = await axios.post(
        `${config.apiBaseUrl}/trials/store`,
        payload,
        {
          headers: {
            Authorization: freshToken ? `Bearer ${freshToken}` : undefined,
            'Content-Type': 'application/json'
          },
        }
      );
      // Redirect to the new trial view page if successful
      if (res.data && res.data.id) {
        navigate(`/trials/${res.data.id}`);
      } else {
        setCreateError('The trial was created, but no id came back. Find it in Trials.');
      }
    } catch (err) {
      console.error('Create trial error:', err);
      // The API's reason for validation errors (e.g. "Trial number already exists.", or a project
      // that doesn't belong to the PO); a 500's message is a raw exception, not for users
      const status = err?.response?.status;
      setCreateError(status === 422 && err.response.data?.message
        ? err.response.data.message
        : `Could not create the trial${status ? ` (server error ${status})` : ''}. Please try again.`);
    } finally {
      setCreating(false);
    }
  };

  // List of required (non-nullable) fields for trial creation
  const requiredTrialFields = [
    'customer_id',
    'projectno',
    'trialno',
    'Harvest',
    'Destruction',
    'TrialSetupARM',
    'Borders_Sprayed',
    'SubcEPA',
    'StOut',
    'ResAr',
    'FinalReport',
    'PegsUp',
    'PlotSize',
    'WOA',
    'DraftReport',
    'GerminationTest',
    'NIT',
    'ReadyForBilling',
    'AdditionalWork',
    'TGW',
    'RecordCleansing',
    'HLLWonly',
    'EndFieldPhase',
    'ARMReady',
    'ReportProofRead',
    'TrialDone',
  ];

  // List of nullable fields for trial creation
  const nullableTrialFields = [
    'studydirector',
    'pitxt',
    'subcontractor',
    'testitem',
    'country_id',
    'crop_id',
    'locationtarget',
    'study_type_id',
    'trial_type_id',
    'host_id',
    'Hours',
    'FT',
    'PI',
    'TrialAddress',
    'LocalReference',
    'EarthSample',
    'TreatmentCount',
    'ReplicationCount',
    'ApplicationCount',
    'VisitCount',
    'FieldFaseStart',
    'FieldFaseEnd',
    'CustomerContact',
    'PlotSizeL',
    'PlotSizeB',
    'Year',
    'subscribed_GEP_GL',
    'unsubscribed_GEP_GL',
    'Weighed_out_Applications',
    'TS',
    'NIT_Samples',
    'GT_Samples',
    'TGW_Samples',
    'Contract_Amount',
    'Noter1',
    'Noter2',
    'Noter3',
    'PosGPSLatitude',
    'PosGPSLongitude',
  ];

  // Only require customer_id, project_id, and trialno to create a trial
  const isTrialValid = (trialForm) => {
    const required = ['customer_id', 'project_id', 'trialno'];
    const missing = [];

    for (const k of required) {
      if (trialForm[k] === undefined || trialForm[k] === null || trialForm[k] === "") {
        missing.push(k);
      }
    }

    const isValid = missing.length === 0;
    console.log('Trial validation:', { isValid, missing, trialForm });
    return isValid;
  };

  // Get missing required fields for display
  const getMissingFields = () => {
    const required = ['customer_id', 'project_id', 'trialno'];
    const missing = [];

    for (const k of required) {
      if (trialForm[k] === undefined || trialForm[k] === null || trialForm[k] === "") {
        missing.push(k);
      }
    }

    return missing;
  };

  // Check if specific field is missing
  const isFieldMissing = (fieldName) => {
    const value = trialForm[fieldName];
    return value === undefined || value === null || value === "";
  };

  // Human-readable field names
  const fieldLabels = {
    customer_id: 'Customer',
    project_id: 'Project',
    trialno: 'Trial Number'
  };

  const isStepValid = () => {
    const result = (() => {
      if (step === 1) return !!(formData.customer && formData.customer.id);
      if (step === 2) return !!(formData.contract && formData.contract.id);
      if (step === 3) return !!(formData.project && formData.project.id);
      if (step === 4) return isTrialValid(trialForm);
      return true; // Default to true for unknown steps
    })();
    console.log('Step validation:', { step, isValid: result, formData, trialForm });
    return result;
  };

  // Automatically set projectno from the previous step
  useEffect(() => {
    if (formData.project) {
      const projectno = formData.project.protocolno || formData.project.projectno;
      if (projectno) {
        setTrialForm(f => ({ ...f, projectno: projectno }));
      }
    }
  }, [formData.project]);

  // Fetch next trial number when reaching step 4
  useEffect(() => {
    const projectno = formData.project?.protocolno || formData.project?.projectno;
    console.log('[TrialNew] useEffect triggered - step:', step, 'projectno:', projectno);

    if (step === 4 && formData.project && projectno) {
      console.log('[TrialNew] Fetching next trial number for project:', projectno);

      axios.get(
        `${config.apiBaseUrl}/trials/nexttrialno/${projectno.split('/').map(encodeURIComponent).join('/')}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      )
        .then(response => {
          // Handle different response formats - API might return string directly or in data property
          let nextTrialNo = response.data;

          // If response.data is an object with a trialno or nextTrialNo property
          if (typeof nextTrialNo === 'object' && nextTrialNo !== null) {
            nextTrialNo = nextTrialNo.trialno || nextTrialNo.nextTrialNo || nextTrialNo.next_trial_no || nextTrialNo.data;
          }

          console.log('Next trial number from API:', nextTrialNo);
          console.log('Full API response:', response.data);

          // Ensure we have a string value
          if (nextTrialNo && typeof nextTrialNo === 'string') {
            setTrialForm(f => (f.trialno ? f : { ...f, trialno: nextTrialNo })); // keep a number the user typed
          } else {
            console.warn('Invalid trial number format received:', nextTrialNo);
            const fallbackTrialNo = `${projectno}-01`;
            console.warn('Using fallback trial number:', fallbackTrialNo);
            setTrialForm(f => (f.trialno ? f : { ...f, trialno: fallbackTrialNo }));
          }
        })
        .catch(error => {
          console.error('Error fetching next trial number:', error);
          // Fallback: generate locally if API fails
          const fallbackTrialNo = `${projectno}-01`;
          console.warn('Using fallback trial number:', fallbackTrialNo);
          setTrialForm(f => (f.trialno ? f : { ...f, trialno: fallbackTrialNo }));
        });
    }
  }, [step, formData.project, token]);

  // Automatically set studydirector to the current user ID from sessionStorage userInfo
  useEffect(() => {
    try {
      const userInfoStr = sessionStorage.getItem('userInfo');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        const currentUserId = userInfo?.data?.id;

        if (currentUserId && !trialForm.studydirector) {
          setTrialForm(f => ({ ...f, studydirector: currentUserId }));
          console.log('Setting study director to current user from userInfo:', currentUserId);
        }
      }
    } catch (error) {
      console.error('Error parsing userInfo from sessionStorage:', error);
    }
  }, [trialForm.studydirector]);

  // Automatically set studydirector to the current user ID (from sessionStorage userInfo) when step 4 is reached
  useEffect(() => {
    if (step === 4 && !leftStep4.current) {
      try {
        const userInfoStr = sessionStorage.getItem('userInfo');
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          const currentUserId = userInfo?.data?.id;

          if (currentUserId) {
            // Force update the studydirector value when step 4 is reached
            setTimeout(() => {
              setTrialForm(f => ({ ...f, studydirector: currentUserId }));
              console.log('Auto-set study director to current user in step 4:', currentUserId);
            }, 0);
          }
        }
      } catch (error) {
        console.error('Error parsing userInfo from sessionStorage:', error);
      }
    }
  }, [step]);

  // Make sure study director is set after studyDirectors are loaded in step 4
  useEffect(() => {
    if (step === 4 && studyDirectors.length > 0 && !leftStep4.current) {
      try {
        const userInfoStr = sessionStorage.getItem('userInfo');
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          const currentUserId = userInfo?.data?.id;

          if (currentUserId) {
            setTrialForm(f => ({ ...f, studydirector: currentUserId }));
            console.log('Set study director after directors loaded:', currentUserId);
          }
        }
      } catch (error) {
        console.error('Error parsing userInfo from sessionStorage:', error);
      }
    }
  }, [step, studyDirectors]);

  // Direct DOM manipulation for Study Director dropdown to ensure it's set
  useEffect(() => {
    if (step === 4) {
      // Wait for the DOM to be ready
      setTimeout(() => {
        try {
          const userInfoStr = sessionStorage.getItem('userInfo');
          if (userInfoStr) {
            const userInfo = JSON.parse(userInfoStr);
            const currentUserId = userInfo?.data?.id;

            if (currentUserId) {
              // Force-set the select element's value directly
              const studyDirectorSelect = document.querySelector('select[name="studydirector"]');
              if (studyDirectorSelect) {
                studyDirectorSelect.value = String(currentUserId);
                // Trigger a change event to ensure React state is updated
                const event = new Event('change', { bubbles: true });
                studyDirectorSelect.dispatchEvent(event);
                console.log('Directly set Study Director dropdown value:', currentUserId);
              }
            }
          }
        } catch (error) {
          console.error('Error setting Study Director dropdown:', error);
        }
      }, 500); // Give DOM time to render
    }
  }, [step]);

  // Initialize map for GPS location when step 4 is reached
  useEffect(() => {
    if (step === 4) {
      // Wait for the DOM to be ready
      setTimeout(() => {
        try {
          const L = window.L; // Use Leaflet from global scope
          if (!L) {
            console.error('Leaflet library not loaded');
            return;
          }

          const mapContainer = document.getElementById('location-map');
          if (!mapContainer) return;

          if (mapRef.current) {
            mapRef.current.remove();
          }

          // Default location if none set (center of map)
          // (a copied old trial may have its position stored the old way - see utils/gps.js)
          const position = parseGps(trialForm.PosGPSLatitude, trialForm.PosGPSLongitude);
          let lat = position?.lat ?? 51.5074;
          let lng = position?.lng ?? 0.1278;

          // Initialize map
          const map = L.map('location-map').setView([lat, lng], 10);
          mapRef.current = map;

          // Add tile layer (OpenStreetMap)
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }).addTo(map);

          // Add marker if coordinates exist
          if (position) {
            markerRef.current = L.marker([position.lat, position.lng]).addTo(map);
          }

          // Add click event to update marker position
          map.on('click', function (e) {
            const { lat, lng } = e.latlng;

            // Update marker
            if (markerRef.current) {
              markerRef.current.setLatLng([lat, lng]);
            } else {
              markerRef.current = L.marker([lat, lng]).addTo(map);
            }

            // Update form
            setTrialForm(f => ({
              ...f,
              PosGPSLatitude: lat.toFixed(6),
              PosGPSLongitude: lng.toFixed(6)
            }));
          });

          // Handle map resize
          setTimeout(() => {
            map.invalidateSize();
          }, 200);
        } catch (error) {
          console.error('Error initializing map:', error);
        }
      }, 500);
    }
  }, [step, trialForm.PosGPSLatitude, trialForm.PosGPSLongitude]);

  return (
    <Layout>
      <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow p-8 min-h-9/10 overflow-auto flex flex-col">
        {trialFetchError && (
          <div className="mb-4 p-3 rounded bg-red-100 text-red-800 font-semibold">
            {trialFetchError}
          </div>
        )}
        {/* Progress Bar */}
        <nav className="space-y-2 mb-6">
          <div
            className="flex h-1 w-full items-center overflow-hidden rounded-full bg-emerald-100 dark:bg-emerald-900"
          >
            <div
              role="progressbar"
              aria-valuenow={(step / steps.length) * 100}
              aria-valuemin="0"
              aria-valuemax="100"
              className="flex items-center justify-center self-stretch rounded-full bg-emerald-600 text-xs font-medium text-white transition-all duration-500 ease-out"
              style={{ width: `${(step / steps.length) * 100}%` }}
            ></div>
          </div>
          <ol className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-4 lg:grid-cols-6" role="list">
            {steps.map((s, idx) => {
              // Allow navigation between steps 1-3 always; step 4 needs customer, CO and project, step 5 the trial data too
              let eligible = true;
              const chainChosen = !!(formData.customer && formData.customer.id && formData.contract && formData.contract.id && formData.project && formData.project.id);
              if (s.id === 4) eligible = chainChosen;
              if (s.id >= 5) eligible = chainChosen && isTrialValid(trialForm);
              // Steps 1-3 always clickable
              // Prepare step labels
              let stepLabel = null;
              if (s.id === 1 && formData.customer && formData.customer.label) {
                stepLabel = <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-1 text-center">{formData.customer.label}</div>;
              }
              if (s.id === 2 && formData.contract) {
                stepLabel = <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-1 text-center">{formData.contract.co}</div>;
              }
              if (s.id === 3 && formData.project && (formData.project.protocolno)) {
                stepLabel = <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-1 text-center">{formData.project.protocolno}</div>;
              }
              return (
                <li key={s.id}>
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => eligible && setStep(s.id)}
                      disabled={!eligible}
                      className={`flex items-center justify-between gap-2 rounded-lg border px-4 py-2 text-sm leading-6 transition-all duration-300 ${step === s.id
                        ? 'border-gray-200 bg-gray-50 text-gray-800 dark:border-gray-700 dark:bg-gray-700/50 dark:text-gray-300'
                        : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:text-gray-900 hover:shadow-xs focus:ring-3 focus:ring-gray-300/25 active:border-gray-200 active:shadow-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:text-gray-200 dark:focus:ring-gray-600/40 dark:active:border-gray-700'
                        } ${!eligible ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="grow">
                        <div className="font-semibold">{s.title}</div>
                        <div>{s.description}</div>
                      </div>
                      {step > s.id && (
                        <div className="flex-none text-emerald-600">
                          <svg
                            className="inline-block size-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.704 6.292a1 1 0 00-1.408-1.416l-5.3 5.28-2.3-2.3a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l6-5.978z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </button>
                    {stepLabel}
                  </div>
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Step Content */}
        <div className="mb-6 flex-1 min-h-0">
          {step === 1 && (
            <div>
              <h2 className="text-xl font-bold mb-4">
                {formData.customer ? (
                  <span className="text-emerald-700 dark:text-emerald-300">{formData.customer.label}</span>
                ) : (
                  'Select Customer'
                )}
              </h2>
              <div className={`w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 flex flex-col min-h-0 max-h-[60vh]`}>
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 bg-gray-50 dark:bg-gray-900 rounded-t-lg">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" /></svg>
                  <input
                    type="text"
                    placeholder="Search customers..."
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-emerald-400 focus:outline-none bg-white dark:bg-gray-800"
                    onChange={e => handleSearchChange(e.target.value)}
                    value={searchTerm}
                    disabled={!!formData.customer}
                  />
                  {formData.customer && (
                    <button
                      className="ml-2 px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 text-xs"
                      onClick={() => {
                        handleFormChange('customer', null);
                        setSearchTerm("");
                      }}
                      type="button"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {!formData.customer && (
                  <ul className="flex-1 min-h-0 overflow-auto divide-y divide-gray-100 dark:divide-gray-800 custom-scrollbar px-4">
                    {filteredCustomers.length === 0 ? (
                      <li className="p-4 text-center text-gray-400">No customers found</li>
                    ) : filteredCustomers.map(customer => (
                      <li
                        key={customer.id}
                        className="flex items-center gap-4 py-3 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 cursor-pointer transition-colors"
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-800 dark:text-gray-100">{customer.label}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {formData.customer && (
                  <div className="p-4 flex items-center gap-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-b-lg">
                    <div className="flex flex-col">
                      <span className="font-semibold text-emerald-700 dark:text-emerald-300">{formData.customer.label}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-bold mb-4">Select {CO_LABEL}</h2>
              <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
                <p id="po-select-label" className="block mb-2 font-semibold text-gray-700 dark:text-gray-200">
                  {CO_LABEL} for: <span className="text-emerald-700 dark:text-emerald-300">{formData.customer?.label || 'No customer selected'}</span>
                </p>
                <div aria-labelledby="po-select-label">
                  <SearchSelect
                    options={sortedContracts}
                    value={formData.contract ? formData.contract.id : null}
                    onChange={(contract) => handleFormChange('contract', contract || null)}
                    getOptionLabel={coLabel}
                    getOptionValue={getId}
                    placeholder={contracts.length === 0 ? 'None found' : 'Search number or description...'}
                    emptyText="No match"
                    disabled={contracts.length === 0}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setNewDialog('po')}
                  className="mt-3 text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-300"
                >
                  + New {CO_LABEL}
                </button>
              </div>
            </div>
          )}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-bold mb-4">Select Project</h2>
              <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
                <label htmlFor="project-select" className="block mb-2 font-semibold text-gray-700 dark:text-gray-200">
                  Projects for: <span className="text-emerald-700 dark:text-emerald-300">{formData.contract ? coLabel(formData.contract) : 'Nothing selected'}</span>
                </label>
                <select
                  id="project-select"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-emerald-400 focus:outline-none bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                  value={formData.project ? formData.project.id : ''}
                  onChange={e => {
                    const selected = projects.find(p => String(p.id) === e.target.value);
                    handleFormChange('project', selected || null);
                  }}
                  disabled={projects.length === 0}
                >
                  <option value="" disabled>
                    {projects.length === 0 ? 'No projects found' : 'Select a project...'}
                  </option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.protocolno || '(no protocol no)'}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setNewDialog('project')}
                  disabled={!formData.contract}
                  className="mt-3 text-sm font-medium text-emerald-700 hover:underline disabled:opacity-50 dark:text-emerald-300"
                >
                  + New project
                </button>
              </div>
            </div>
          )}
          {step === 4 && (
            <div>
              <h2 className="text-xl font-bold mb-4">Trial setup</h2>

              {/* Validation Summary Banner */}
              {getMissingFields().length > 0 && (
                <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-2">
                        Please complete the following required fields:
                      </h3>
                      <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-400 space-y-1">
                        {getMissingFields().map(field => (
                          <li key={field}>{fieldLabels[field] || field}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Trial Info Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 shadow-sm border border-emerald-100 dark:border-emerald-800">
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-300 uppercase tracking-wide mb-2">Customer</h3>
                  <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-200">{formData.customer?.label || 'Not selected'}</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 shadow-sm border border-emerald-100 dark:border-emerald-800">
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-300 uppercase tracking-wide mb-2">{CO_LABEL}</h3>
                  <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-200">
                    {formData.contract ? coLabel(formData.contract) : 'Not selected'}
                  </p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 shadow-sm border border-emerald-100 dark:border-emerald-800">
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-300 uppercase tracking-wide mb-2">Project</h3>
                  <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-200">
                    {
                      formData.project?.protocolno ||
                      'Not selected'}
                  </p>
                </div>
              </div>

              {/* GPS Card */}
              <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-700 px-4 py-3">
                  <h3 className="text-white font-medium">GPS Location</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Latitude</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:text-white"
                        placeholder="51.5074"
                        value={trialForm.PosGPSLatitude || ''}
                        onChange={e => {
                          const newLat = e.target.value;
                          setTrialForm(f => ({ ...f, PosGPSLatitude: newLat }));

                          // Update marker if map exists and value is valid
                          if (mapRef.current && markerRef.current && !isNaN(newLat)) {
                            const lng = trialForm.PosGPSLongitude || 0;
                            markerRef.current.setLatLng([newLat, lng]);
                            mapRef.current.setView([newLat, lng], mapRef.current.getZoom());
                          }
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Longitude</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:text-white"
                        placeholder="0.1278"
                        value={trialForm.PosGPSLongitude || ''}
                        onChange={e => {
                          const newLng = e.target.value;
                          setTrialForm(f => ({ ...f, PosGPSLongitude: newLng }));

                          // Update marker if map exists and value is valid
                          if (mapRef.current && markerRef.current && !isNaN(newLng)) {
                            const lat = trialForm.PosGPSLatitude || 0;
                            markerRef.current.setLatLng([lat, newLng]);
                            mapRef.current.setView([lat, newLng], mapRef.current.getZoom());
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Get Current Location Button */}
                  <div className="mb-4">
                    <button
                      type="button"
                      className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 flex items-center"
                      onClick={() => {
                        if (navigator.geolocation) {
                          navigator.geolocation.getCurrentPosition(
                            (position) => {
                              const { latitude, longitude } = position.coords;

                              // Update form
                              setTrialForm(f => ({
                                ...f,
                                PosGPSLatitude: latitude.toFixed(6),
                                PosGPSLongitude: longitude.toFixed(6)
                              }));

                              // Update map
                              if (mapRef.current) {
                                mapRef.current.setView([latitude, longitude], 15);

                                // Update marker
                                if (markerRef.current) {
                                  markerRef.current.setLatLng([latitude, longitude]);
                                } else {
                                  markerRef.current = L.marker([latitude, longitude]).addTo(mapRef.current);
                                }
                              }
                            },
                            (error) => {
                              console.error("Error getting location:", error);
                              alert(`Could not get your location: ${error.message}`);
                            },
                            { enableHighAccuracy: true }
                          );
                        } else {
                          alert("Geolocation is not supported by this browser.");
                        }
                      }}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                      Get My Location
                    </button>
                  </div>

                  {/* Map Container */}
                  <div id="location-map" className="w-full h-64 rounded-lg border border-gray-300 dark:border-gray-600 mb-4"></div>

                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-md text-sm text-gray-500 dark:text-gray-300">
                    <p className="flex items-center">
                      <svg className="w-4 h-4 mr-1 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Click on the map to set the GPS coordinates, or enter them manually.
                    </p>
                  </div>
                </div>
              </div>

              {/* Enter goes on to the tasks; the trial is created in the last step */}
              <form className="space-y-6" onSubmit={e => { e.preventDefault(); if (isTrialValid(trialForm)) handleNext(); }}>
                {/* Hidden fields for IDs */}
                <input type="hidden" name="customer_id" value={formData.customer?.id || ''} />
                <input type="hidden" name="project_id" value={formData.project?.id || ''} />

                {/* Required fields section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-visible border border-gray-200 dark:border-gray-700">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-4 py-3">
                    <h3 className="text-white font-medium">Required Information</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Trial Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Trial Number <span className="text-red-600">*</span>
                        {isFieldMissing('trialno') && (
                          <span className="text-xs text-red-600 ml-2">(Required)</span>
                        )}
                      </label>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 rounded-md border focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${isFieldMissing('trialno')
                          ? 'border-red-500 dark:border-red-500 focus:border-red-500'
                          : 'border-gray-300 dark:border-gray-600 focus:border-blue-500'
                          }`}
                        value={trialForm.trialno || ''}
                        name="trialno"
                        maxLength={TRIAL_MAX_LENGTH.trialno}
                        onChange={e => setTrialForm(f => ({ ...f, trialno: e.target.value }))}
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Auto-generated based on project number (max 100 characters)</p>
                    </div>

                    {/* Year: the PO's year, unless changed; a different year is only a warning */}
                    <div>
                      <label htmlFor="trial-year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Year</label>
                      <input
                        id="trial-year"
                        type="number"
                        name="Year"
                        {...YEAR_NUMBER}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.Year ?? ''}
                        onChange={e => {
                          setYearTouched(true);
                          setTrialForm(f => ({ ...f, Year: e.target.value === '' ? null : Number(e.target.value) }));
                        }}
                      />
                      {yearTouched && yearWarning(trialForm.Year, formData.contract) && (
                        <p role="status" data-testid="year-warning" className="mt-1 text-xs text-amber-700 dark:text-amber-300">{yearWarning(trialForm.Year, formData.contract)}</p>
                      )}
                    </div>

                    {/* Study Director */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Study Director
                        <span className="text-xs text-emerald-600 ml-2">(Automatically set to you)</span>
                      </label>
                      <SearchSelect
                        options={studyDirectors.map(d => ({ value: d.id, label: d.name || d.firstname || d.initials || d.email || '(unnamed user)' }))}
                        name="studydirector"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.studydirector || (JSON.parse(sessionStorage.getItem('userInfo') || '{}')?.data?.id) || ''}
                        onChange={(opt) => setTrialForm(f => ({ ...f, studydirector: opt ? opt.value : null }))}
                        defaultValue={(JSON.parse(sessionStorage.getItem('userInfo') || '{}')?.data?.id) || ''}
                      >
                        {/* <option value="">Select Study Director</option>
                        {studyDirectors.map(director => {
                          // Check if this is the current user
                          const isCurrentUser = String(director.id) === String(localStorage.getItem('userId'));
                          const directorName = director.name || director.firstname || director.email || `ID: ${director.id}`;
                          
                          return (
                            <option key={director.id} value={director.id}>
                              {directorName}{isCurrentUser ? ' (You)' : ''}
                            </option>
                          );
                        })} */}
                      </SearchSelect>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        The Study Director is set to your user account by default
                      </p>
                    </div>

                    {/* Trial Types and Details */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
                      <select
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.country_id || ''}
                        onChange={e => setTrialForm(f => ({ ...f, country_id: e.target.value }))}
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country.id} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Crop</label>
                      <SearchSelect
                        options={crops.map(c => ({ value: c.id, label: c.name + (c.description ? ` (${c.description})` : '') }))}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.crop_id || ''}
                        onChange={(opt) => setTrialForm(f => ({ ...f, crop_id: opt ? opt.value : null }))}
                      >
                      </SearchSelect>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Study Type</label>
                      <SearchSelect
                        options={studyTypes.map(t => ({ value: t.id, label: t.name }))}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.study_type_id || ''}
                        onChange={(opt) => setTrialForm(f => ({ ...f, study_type_id: opt ? opt.value : null }))}
                      >
                      </SearchSelect>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Trial Type</label>
                      <SearchSelect
                        options={trialTypes.map(t => ({ value: t.id, label: t.name }))}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.trial_type_id || ''}
                        onChange={(opt) => setTrialForm(f => ({ ...f, trial_type_id: opt ? opt.value : null }))}
                      >
                      </SearchSelect>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Host</label>
                      <SearchSelect
                        options={hosts.map(h => ({ value: h.id, label: h.name }))}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.host_id || ''}
                        onChange={(opt) => setTrialForm(f => ({ ...f, host_id: opt ? opt.value : null }))}
                        placeholder="Select host..."
                      >
                      </SearchSelect>
                      <button
                        type="button"
                        onClick={() => setNewDialog('host')}
                        className="mt-1 text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-300"
                      >
                        + New host
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Principal Investigator (PI)</label>
                      <SearchSelect
                        options={studyDirectors.map(d => ({ value: d.id, label: d.initials ? `${d.name} (${d.initials})` : (d.name || d.email || '(unnamed user)') }))}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.PI || ''}
                        onChange={(opt) => setTrialForm(f => ({ ...f, PI: opt ? opt.value : null }))}
                        placeholder="Select PI..."
                      >
                      </SearchSelect>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Field Technician (FT)</label>
                      <SearchSelect
                        options={studyDirectors.map(d => ({ value: d.id, label: d.initials ? `${d.name} (${d.initials})` : (d.name || d.email || '(unnamed user)') }))}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.FT || ''}
                        onChange={(opt) => setTrialForm(f => ({ ...f, FT: opt ? opt.value : null }))}
                        placeholder="Select FT..."
                      >
                      </SearchSelect>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location Target</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.locationtarget || ''}
                        name="locationtarget"
                        maxLength={TRIAL_MAX_LENGTH.locationtarget}
                        onChange={e => setTrialForm(f => ({ ...f, locationtarget: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Trial Specifications */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
                  <div className="bg-gradient-to-r from-purple-500 to-purple-700 px-4 py-3">
                    <h3 className="text-white font-medium">Trial Specifications</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Treatment Count</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.TreatmentCount || ''}
                        name="TreatmentCount"
                        {...NON_NEGATIVE_NUMBER}
                        onChange={e => setTrialForm(f => ({ ...f, TreatmentCount: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Replication Count</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.ReplicationCount || ''}
                        name="ReplicationCount"
                        {...NON_NEGATIVE_NUMBER}
                        onChange={e => setTrialForm(f => ({ ...f, ReplicationCount: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Application Count</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.ApplicationCount || ''}
                        name="ApplicationCount"
                        {...NON_NEGATIVE_NUMBER}
                        onChange={e => setTrialForm(f => ({ ...f, ApplicationCount: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Visit Count</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.VisitCount ?? ''}
                        name="VisitCount"
                        {...NON_NEGATIVE_NUMBER}
                        onChange={e => setTrialForm(f => ({ ...f, VisitCount: e.target.value }))}
                      />
                      <p className="mt-1 text-xs text-gray-500">Visit 00 plus this many visits</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plot Size L</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.PlotSizeL || ''}
                        name="PlotSizeL"
                        {...NON_NEGATIVE_NUMBER}
                        onChange={e => setTrialForm(f => ({ ...f, PlotSizeL: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plot Size W</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                        value={trialForm.PlotSizeB || ''}
                        name="PlotSizeB"
                        {...NON_NEGATIVE_NUMBER}
                        onChange={e => setTrialForm(f => ({ ...f, PlotSizeB: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Trial Details */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
                  <div className="bg-gradient-to-r from-amber-500 to-amber-700 px-4 py-3">
                    <h3 className="text-white font-medium">Trial Details</h3>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* The PI is chosen above; old trials (copied) may still carry it as free text */}
                      {trialForm.pitxt ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PI (old text)</label>
                          <textarea
                            className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-gray-700 dark:text-white"
                            rows="3"
                            value={trialForm.pitxt || ''}
                            name="pitxt"
                            maxLength={TRIAL_MAX_LENGTH.pitxt}
                            onChange={e => setTrialForm(f => ({ ...f, pitxt: e.target.value }))}
                          ></textarea>
                        </div>
                      ) : null}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Trial Address</label>
                        <textarea
                          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-gray-700 dark:text-white"
                          rows="3"
                          value={trialForm.TrialAddress || ''}
                          name="TrialAddress"
                          maxLength={TRIAL_MAX_LENGTH.TrialAddress}
                          onChange={e => setTrialForm(f => ({ ...f, TrialAddress: e.target.value }))}
                        ></textarea>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Field Phase Start</label>
                        <DatePicker
                          label="Field Phase Start"
                          selected={parseYMDToLocalDate(trialForm.FieldFaseStart)}
                          startMonth={FIELD_PHASE_START_MONTH}
                          endMonth={FIELD_PHASE_END_MONTH}
                          onChange={date => setTrialForm(f => ({ ...f, FieldFaseStart: formatDateToYMD(date) }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Field Phase End</label>
                        <DatePicker
                          label="Field Phase End"
                          minDate={parseYMDToLocalDate(trialForm.FieldFaseStart)}
                          selected={parseYMDToLocalDate(trialForm.FieldFaseEnd)}
                          startMonth={FIELD_PHASE_START_MONTH}
                          endMonth={FIELD_PHASE_END_MONTH}
                          onChange={date => setTrialForm(f => ({ ...f, FieldFaseEnd: formatDateToYMD(date) }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subcontractor</label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-gray-700 dark:text-white"
                          value={trialForm.subcontractor || ''}
                          name="subcontractor"
                          maxLength={TRIAL_MAX_LENGTH.subcontractor}
                          onChange={e => setTrialForm(f => ({ ...f, subcontractor: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Test Item</label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-gray-700 dark:text-white"
                          value={trialForm.testitem || ''}
                          name="testitem"
                          maxLength={TRIAL_MAX_LENGTH.testitem}
                          onChange={e => setTrialForm(f => ({ ...f, testitem: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Local Reference</label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-gray-700 dark:text-white"
                          value={trialForm.LocalReference || ''}
                          name="LocalReference"
                          maxLength={TRIAL_MAX_LENGTH.LocalReference}
                          onChange={e => setTrialForm(f => ({ ...f, LocalReference: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trial Status */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
                  <div className="bg-gradient-to-r from-gray-500 to-gray-700 px-4 py-3">
                    <h3 className="text-white font-medium">Trial Status</h3>
                  </div>
                  {/* Under the old system's headings (Trial/Harvest/Report Activities, then Other) */}
                  {ACTIVITY_CATEGORIES.map(category => ({
                    ...category,
                    fields: [
                      'Destruction', 'TrialSetupARM', 'Borders_Sprayed',
                      // Not Closed or Subscribed/Unsubscribed GEP: a new trial is open, and GEP is set later
                      'SubcEPA', 'StOut', 'ResAr', 'FinalReport',
                      'PegsUp', 'PlotSize', 'WOA',
                      // Not Harvest, NIT, Germination Test or TGW: Yes / No / To be decided (above)
                      'DraftReport', 'ReadyForBilling',
                      'AdditionalWork', 'RecordCleansing', 'HLLWonly',
                      'EndFieldPhase', 'ARMReady', 'ReportProofRead', 'TrialDone'
                    ].filter(field => (vanoCodes.find(code => code.field === field)?.category ?? null) === category.key),
                  })).filter(group => group.fields.length > 0).map(group => (
                  <div key={group.title} className="px-4 pt-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{group.title}</h4>
                  <div className="py-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Harvest, NIT, Germination Test and TGW are Yes / No / To be decided, among the Harvest Activities */}
                    {group.key === 'harvest' && TRI_STATE_FIELDS.map(field => {
                      // The activity's samples beside it: only while it is Yes (else off and empty, not sent)
                      const samples = SAMPLE_FIELDS.find((s) => s.flag === field);
                      return (
                        <div key={field}>
                          <TriStateSelect
                            id={field}
                            label={activityLabel(vanoCodes, field, field)}
                            value={trialForm[field]}
                            onChange={(value) => setTrialForm((f) => ({ ...f, [field]: value }))}
                          />
                          {samples && (
                            <div className="mt-2">
                              <label htmlFor={samples.field} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{samples.label}</label>
                              <input
                                type="number"
                                id={samples.field}
                                {...NON_NEGATIVE_NUMBER}
                                disabled={!takesSamples(trialForm, field)}
                                value={shownSamples(trialForm, samples.field) ?? ''}
                                onChange={(e) => setTrialForm((f) => ({ ...f, [samples.field]: e.target.value }))}
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {group.fields.map(field => (
                      <div key={field} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={field}
                          checked={!!trialForm[field]}
                          onChange={e => setTrialForm(f => ({ ...f, [field]: e.target.checked }))}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <label htmlFor={field} className="text-sm text-gray-700 dark:text-gray-300">
                          {activityLabel(vanoCodes, field, field.replace(/_/g, ' '))}
                        </label>
                      </div>
                    ))}
                  </div>
                  </div>
                  ))}
                </div>

              </form>
            </div>
          )}
          {step === 5 && (
            <div>
              <h2 className="text-xl font-bold mb-2">Tasks & time intervals</h2>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                The tasks the trial will get, from Visit Count, Application Count and the activities in step 4.
                Give the Time interval where it is known, set from the customer's agreement: it is locked once the trial is created.
              </p>
              <p className="mb-4 text-sm font-medium" data-testid="wizard-task-count">
                {taskCount} {taskCount === 1 ? 'task' : 'tasks'} · {datedCount} with a time interval
              </p>
              <div className="space-y-4">
                {taskGroups.map(group => (
                  <section
                    key={group.title}
                    aria-label={group.title}
                    className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                  >
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{group.title}</h3>
                    <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                      {group.tasks.map(task => (
                        <li key={task.vano} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid="wizard-task">
                          <span className="min-w-0">
                            <span className="font-medium">{task.name}</span>
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">VA {task.vano}</span>
                          </span>
                          <span className="min-w-0">
                            <PlannedInput
                              kind="timeinterval"
                              idPrefix={`wizard-interval-${task.vano}`}
                              dateLabel={`Time interval for ${task.name}`}
                              startMonth={EXPECTED_START_MONTH}
                              endMonth={EXPECTED_END_MONTH}
                              value={{ date: diaryDates[task.vano] || '', text: diaryTexts[task.vano] || '' }}
                              onChange={({ date, text }) => {
                                setDiaryDates(d => ({ ...d, [task.vano]: date }));
                                setDiaryTexts(t => ({ ...t, [task.vano]: text }));
                              }}
                            />
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </div>
          )}
          {step === 6 && (
            <div>
              <h2 className="text-xl font-bold mb-2">Review & create</h2>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">Check the trial before it is created. "Change" goes back to that step.</p>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {[
                  ['Customer', formData.customer?.label],
                  [CO_LABEL, formData.contract ? coLabel(formData.contract) : ''],
                  ['Project', formData.project?.protocolno],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-emerald-100 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20">
                    <h3 className="text-xs font-medium uppercase tracking-wide text-emerald-800 dark:text-emerald-300">{label}</h3>
                    <p className="mt-1 font-semibold text-emerald-700 dark:text-emerald-200">{value || '—'}</p>
                  </div>
                ))}
              </div>

              {[
                {
                  title: 'Trial', step: 4, rows: [
                    ['Trial No', trialForm.trialno],
                    ['Year', trialForm.Year],
                    ['Crop', reviewName(crops, trialForm.crop_id, c => c.name + (c.description ? ` (${c.description})` : ''))],
                    ['Country', reviewName(countries, trialForm.country_id)],
                    ['Study Type', reviewName(studyTypes, trialForm.study_type_id)],
                    ['Trial Type', reviewName(trialTypes, trialForm.trial_type_id)],
                    ['Host', reviewName(hosts, trialForm.host_id)],
                    ['Field Phase', [trialForm.FieldFaseStart, trialForm.FieldFaseEnd].filter(Boolean).map(d => formatDate(d)).join(' – ')],
                    ['Treatments', trialForm.TreatmentCount],
                    ['Replications', trialForm.ReplicationCount],
                  ],
                },
                {
                  title: 'People', step: 4, rows: [
                    ['Study Director', reviewName(studyDirectors, trialForm.studydirector, reviewPerson)],
                    ['PI', reviewName(studyDirectors, trialForm.PI, reviewPerson)],
                    ['FT', reviewName(studyDirectors, trialForm.FT, reviewPerson)],
                  ],
                },
              ].map(section => (
                <section key={section.title} aria-label={section.title} className="mt-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{section.title}</h3>
                    <button type="button" onClick={() => setStep(section.step)} aria-label={`Change ${section.title}`} className="min-h-10 rounded border px-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">Change</button>
                  </div>
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                    {section.rows.map(([label, value]) => (
                      <div key={label} className="flex justify-between gap-2 border-b border-gray-100 py-1 dark:border-gray-800">
                        <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
                        <dd className="text-right text-sm font-medium" data-testid={`review-${label}`}>{value === 0 || value ? String(value) : '—'}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}

              <section aria-label="Tasks" className="mt-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Tasks <span className="normal-case tracking-normal">({taskCount} · {datedCount} with a time interval)</span>
                  </h3>
                  <button type="button" onClick={() => setStep(5)} aria-label="Change Tasks" className="min-h-10 rounded border px-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">Change</button>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                      <th className="py-1 font-medium">Task</th>
                      <th className="py-1 font-medium">VA No</th>
                      <th className="py-1 font-medium">Time interval</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {taskGroups.flatMap(group => group.tasks).map(task => (
                      <tr key={task.vano}>
                        <td className="py-1.5">{task.name}</td>
                        <td className="py-1.5 text-gray-500 dark:text-gray-400">{task.vano}</td>
                        <td className="py-1.5">{expectedText(taskInterval(task.vano)) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {createError && (
                <div role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                  {createError}
                </div>
              )}
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={handleCreateTrial}
                  className="min-h-10 px-6 py-3 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  disabled={!isTrialValid(trialForm) || creating}
                >
                  {creating ? 'Creating...' : 'Create Trial'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={step === 1}
            className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {/* The last step ends with "Create Trial" in the form, so it has no Next */}
          {step < steps.length && (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: {steps[step].description}
            </button>
          )}
        </div>
      </div>

      {/* Create what's missing without leaving the wizard; the new item is selected */}
      <ContractModal
        open={newDialog === 'po'}
        onClose={() => setNewDialog(null)}
        customerId={formData.customer?.id}
        customerName={formData.customer?.label}
        onCreated={(contract) => {
          if (!contract) return;
          setContracts(list => [...list, contract]);
          handleFormChange('contract', contract);
        }}
      />
      <ProjectModal
        open={newDialog === 'project'}
        onClose={() => setNewDialog(null)}
        contract={formData.contract}
        onSaved={(project) => {
          if (!project) return;
          setProjects(list => [...list, project]);
          handleFormChange('project', project);
        }}
      />
      <HostModal
        open={newDialog === 'host'}
        onClose={() => setNewDialog(null)}
        onSaved={(host) => {
          if (!host) return;
          setHosts(list => [...list, host]);
          setTrialForm(f => ({ ...f, host_id: host.id }));
        }}
      />
    </Layout>
  );
}
